import { useRef } from 'react';
import { Link } from 'react-router';
import type { RankedPropertyDTO, SortKey } from '@/types/db';
import { GlassPanel } from '@/components/glass/GlassPanel';
import { HeroPhoto } from './HeroPhoto';
import { RankingBadge } from './RankingBadge';
import { RankingBar } from './RankingBar';
import { RecencyBadge } from './RecencyBadge';
import { IncompleteDataBadge } from './IncompleteDataBadge';
import { formatPrice, formatRadius, formatRecencyDays } from '@/lib/format';
import { NEAREST_CATEGORY_BY_VALUE, type NearestCategory } from '@/lib/nearest-constants';
import { cn } from '@/lib/utils';

export interface PropertyCardProps {
  property: RankedPropertyDTO;
  /** Current radius (mi), used in tooltip strings. */
  radius: number;
  /** First card on the page — eager hero load. */
  priority?: boolean;
  /**
   * Optional pre-resolved hero photo URL. Plan 05-05 will resolve from
   * `/static/reports/<addr>/images/zillow/<favorite>.jpg` based on a
   * separate query; for now (Plan 05-04 showcase) callers pass the URL
   * directly or `null` for the fallback.
   *
   * Phase 06 CARRY-2: prefer `heroImageUrl` (from API `hero_image_url`);
   * `photoSrc` retained for showcase compatibility. When both are set,
   * `heroImageUrl` takes precedence.
   */
  photoSrc?: string | null;
  /**
   * Phase 06 CARRY-2 — pre-resolved hero photo URL from `/api/rankings`
   * `hero_image_url`. When provided (even as `null`), takes precedence
   * over `photoSrc`. Closes Phase 05 UI-REVIEW Top Fix #4 (sea-of-Home
   * regression on the dashboard mobile screenshot).
   */
  heroImageUrl?: string | null;
  /** Optional listing key-stats line (beds · baths · sqft · price). */
  keyStatsText?: string;
  /** Active dashboard sort — drives which metric the card surfaces. */
  sort?: SortKey;
  /** Active nearest-amenity target (only meaningful when sort === 'nearest'). */
  nearestCat?: NearestCategory;
  className?: string;
}

/**
 * `<PropertyCard>` — long-row 1100×330 card with full-card click target.
 *
 * Anatomy (UI-SPEC § 3.2 + DASH-03):
 *   `<a href="/app/properties/:id">`                           ← full click target
 *     `<GlassPanel variant="base" grid grid-cols-[440px_1fr]>` ← outer glass
 *       `<HeroPhoto>`                                          ← left 4:3
 *       `<GlassPanel variant="strong" p-6 grid-rows-[…]>`      ← right text panel
 *         row 1: address + RankingBadge
 *         row 2: 3 RankingBars (always visible — Pitfall 10)
 *         row 3: key-stats grid + amenity badges
 *         row 4: RecencyBadge + (optional) IncompleteDataBadge
 *
 * Mount-time motion (DASH-05): on first render, the 3 RankingBars animate
 * from 0% → final % over 400ms ease-out. A `useRef` flag prevents
 * re-animation on parent re-render (e.g. when filter changes propagate
 * through TanStack Query). `prefers-reduced-motion: reduce` skips the
 * animation entirely (handled inside `<RankingBar>`).
 *
 * Hover (UI-SPEC § 1.4): card lifts 2px (`hover:translate-y-[-2px]`),
 * shadow elevates from `shadow-2xl` → `shadow-3xl`, photo `scale-105` via
 * the `group-hover` class on `<HeroPhoto>`.
 *
 * ARIA (§ 9.3): outer `<a aria-label="Open property details for {addr},
 * composite score {composite} of 100">`. Single tab stop per card.
 */
export function PropertyCard({
  property,
  radius,
  priority = false,
  photoSrc = null,
  heroImageUrl,
  keyStatsText,
  sort,
  nearestCat,
  className,
}: PropertyCardProps) {
  // Phase 06 CARRY-2: heroImageUrl wins when explicitly provided (even null).
  // Falls back to legacy photoSrc when heroImageUrl is undefined.
  const resolvedHeroSrc = heroImageUrl !== undefined ? heroImageUrl : photoSrc;
  // First-mount-only flag — survives re-renders triggered by parent state
  // (filters changing, query refetch). Subsequent re-renders pass
  // animate=false to <RankingBar>.
  const hasAnimatedRef = useRef<boolean>(false);
  const animateBars = !hasAnimatedRef.current;
  // Schedule the flip after render so the *first* paint still passes
  // animate=true (the bar manages 0→final width with rAF).
  if (animateBars) {
    queueMicrotask(() => {
      hasAnimatedRef.current = true;
    });
  }

  const { days } = formatRecencyDays(property.created_at);
  const dogCounts = property.amenities_in_radius_by_category;
  const dogCountTotal =
    (dogCounts.dog_park ?? 0) +
    (dogCounts.beach ?? 0) +
    (dogCounts.daycare ?? 0) +
    (dogCounts.bar ?? 0);

  const dogTooltip = `${dogCounts.dog_park ?? 0} dog parks, ${dogCounts.beach ?? 0} dog beaches, ${dogCounts.daycare ?? 0} daycare, ${dogCounts.bar ?? 0} dog-friendly bars within ${formatRadius(radius)}`;
  const walkEntTooltip = `Walkability: ${property.walkability_score ?? '—'}/100 · Entertainment: ${property.entertainment_score ?? '—'}/100`;
  const freshTooltip = `Listed ${days} days ago — bucket: ${days <= 7 ? 'new' : days <= 30 ? 'recent' : days <= 90 ? 'older' : 'stale'}`;

  // Derive walk_ent score: average if both present, else whichever is present.
  const walkScore = property.walkability_score;
  const entScore = property.entertainment_score;
  let walkEntScore: number | null;
  if (walkScore == null && entScore == null) walkEntScore = null;
  else if (walkScore != null && entScore != null)
    walkEntScore = Math.round((walkScore + entScore) / 2);
  else walkEntScore = walkScore ?? entScore ?? null;

  // Sort-aware metric line. Price shows whenever known; the nearest-distance
  // line shows only under the nearest sort ("—"/none-nearby when null).
  const priceText = property.price != null ? formatPrice(property.price) : null;
  const nearestMeta = nearestCat ? NEAREST_CATEGORY_BY_VALUE[nearestCat] : undefined;
  const nd = property.nearest_distance_mi;
  const showNearest = sort === 'nearest' && nearestMeta != null;
  const nearestText = !showNearest
    ? null
    : nd == null
      ? `No ${nearestMeta!.label.toLowerCase()} nearby`
      : `${nd < 0.1 ? '<0.1' : nd.toFixed(1)} mi to nearest ${nearestMeta!.label.toLowerCase()}`;

  return (
    <Link
      to={`/properties/${property.property_id}`}
      // Plan 05-06 a11y fix: aria-label was a curated string with bits of
      // context not present in the visible content (the word "composite",
      // a description "Open details") — axe label-content-name-mismatch
      // fires when visible text inside the link doesn't appear in the
      // accessible name. Drop the aria-label entirely so the link's
      // accessible name is computed from its visible content (heading +
      // bars + badges + recency). This makes SR announcement match what
      // keyboard users see on focus.
      className={cn(
        'group block w-full max-w-[1100px] focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2 rounded-2xl',
        className,
      )}
    >
      <GlassPanel
        variant="base"
        className={cn(
          'grid grid-cols-1 md:grid-cols-[440px_1fr] gap-0 p-0 overflow-hidden',
          'h-auto md:h-[330px] shadow-2xl',
          'transition-transform duration-150 ease-out',
          'hover:translate-y-[-2px] hover:shadow-3xl',
        )}
      >
        <div className="relative w-full md:w-[440px] h-[220px] md:h-full overflow-hidden">
          <HeroPhoto src={resolvedHeroSrc} alt={property.address} priority={priority} />
        </div>
        <GlassPanel
          variant="strong"
          className="p-6 grid grid-rows-[auto_auto_1fr_auto] gap-4 rounded-none border-0"
        >
          {/* Row 1: heading + score badge */}
          <div className="flex items-start justify-between gap-4">
            <h3 className="text-xl font-semibold text-foam-50 leading-tight tracking-tight">
              {property.address}
            </h3>
            <RankingBadge
              score={property.composite}
              isIncomplete={property.is_incomplete}
              dogProximityFactor={property.dog_proximity_factor}
            />
          </div>

          {/* Row 2: always-visible breakdown bars */}
          <div className="grid gap-2">
            <RankingBar
              axis="dog"
              score={Math.round((property.dog_proximity_factor ?? 0) * 100)}
              tooltip={dogTooltip}
              animate={animateBars}
            />
            <RankingBar
              axis="walk_ent"
              score={walkEntScore}
              tooltip={walkEntTooltip}
              animate={animateBars}
            />
            <RankingBar
              axis="fresh"
              score={property.recency_score}
              tooltip={freshTooltip}
              animate={animateBars}
            />
          </div>

          {/* Row 3: key-stats + amenity totals */}
          <div className="flex items-end gap-x-6 gap-y-1 flex-wrap text-sm text-foam-50/80">
            {priceText && (
              <span className="tabular-nums font-semibold text-foam-50">{priceText}</span>
            )}
            {nearestText && <span className="tabular-nums">{nearestText}</span>}
            {keyStatsText && <span>{keyStatsText}</span>}
            <span className="tabular-nums">Dog: {dogCountTotal}</span>
            <span className="tabular-nums">
              Walk + Ent:{' '}
              {walkEntScore == null ? '—' : Math.round(walkEntScore)}
            </span>
            {property.county && (
              <span>
                {property.county.charAt(0).toUpperCase() + property.county.slice(1)} County
              </span>
            )}
          </div>

          {/* Row 4: recency + incomplete badges */}
          <div className="flex items-center gap-2 flex-wrap">
            <RecencyBadge daysSince={days} />
            {property.is_incomplete && <IncompleteDataBadge />}
          </div>
        </GlassPanel>
      </GlassPanel>
    </Link>
  );
}
